import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
    Target, Moon, Apple, Dumbbell, Brain, Heart,
    ChevronRight, ChevronLeft, Sparkles, Check
} from 'lucide-react';

const NANOUK = {
    happy: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    smart: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    trailblazer: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
    sleeping: 'https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop',
};

const quizContent = {
    de: {
        title: "Buddy-Check",
        subtitle: "Lass mich dich besser kennenlernen!",
        questions: [
            {
                id: 'goal',
                question: "Was ist dein Hauptziel?",
                icon: Target,
                nanoukImg: 'trailblazer',
                nanoukSays: "Wuff! Jedes Rudel braucht ein Ziel. Was ist deins?",
                options: [
                    { value: 'weight_loss', label: 'Abnehmen', emoji: '🔥' },
                    { value: 'muscle', label: 'Muskeln aufbauen', emoji: '💪' },
                    { value: 'energy', label: 'Mehr Energie', emoji: '⚡' },
                    { value: 'health', label: 'Gesünder leben', emoji: '🌿' },
                    { value: 'sleep', label: 'Besser schlafen', emoji: '😴' }
                ]
            },
            {
                id: 'fitness_level',
                question: "Wie fit bist du aktuell?",
                icon: Dumbbell,
                nanoukImg: 'smart',
                nanoukSays: "Keine Sorge, ich passe mich deinem Tempo an!",
                options: [
                    { value: 'beginner', label: 'Anfänger', emoji: '🌱' },
                    { value: 'intermediate', label: 'Fortgeschritten', emoji: '🏃' },
                    { value: 'advanced', label: 'Profi', emoji: '🏆' }
                ]
            },
            {
                id: 'sleep_hours',
                question: "Wie viele Stunden schläfst du durchschnittlich?",
                icon: Moon,
                nanoukImg: 'sleeping',
                nanoukSays: "*gähnt* Schlaf ist wichtig für uns beide...",
                options: [
                    { value: '5', label: 'Unter 6 Stunden', emoji: '😵' },
                    { value: '6', label: '6-7 Stunden', emoji: '😐' },
                    { value: '7', label: '7-8 Stunden', emoji: '😊' },
                    { value: '8', label: 'Über 8 Stunden', emoji: '😴' }
                ]
            },
            {
                id: 'nutrition_style',
                question: "Wie ernährst du dich?",
                icon: Apple,
                nanoukImg: 'happy',
                nanoukSays: "Leckerli... äh, ich meine gesundes Essen ist wichtig!",
                options: [
                    { value: 'mixed', label: 'Mischkost', emoji: '🍽️' },
                    { value: 'vegetarian', label: 'Vegetarisch', emoji: '🥗' },
                    { value: 'vegan', label: 'Vegan', emoji: '🌱' },
                    { value: 'low_carb', label: 'Low Carb', emoji: '🥩' },
                    { value: 'intuitive', label: 'Intuitiv', emoji: '🧘' }
                ]
            },
            {
                id: 'motivation',
                question: "Was motiviert dich am meisten?",
                icon: Heart,
                nanoukImg: 'trailblazer',
                nanoukSays: "Gemeinsam schaffen wir alles, Buddy!",
                options: [
                    { value: 'streaks', label: 'Tägliche Streaks', emoji: '🔥' },
                    { value: 'progress', label: 'Sichtbare Fortschritte', emoji: '📈' },
                    { value: 'community', label: 'Community & Challenges', emoji: '👥' },
                    { value: 'rewards', label: 'Belohnungen & Badges', emoji: '🏅' },
                    { value: 'health', label: 'Gesundheit & Wohlbefinden', emoji: '❤️' }
                ]
            }
        ],
        complete: {
            title: "Perfekt, Buddy!",
            subtitle: "Ich kenne dich jetzt besser. Lass uns loslegen!",
            nanoukSays: "Wuff! Wir sind ein großartiges Team!"
        }
    },
    en: {
        title: "Buddy-Check",
        subtitle: "Let me get to know you better!",
        questions: [
            {
                id: 'goal',
                question: "What's your main goal?",
                icon: Target,
                nanoukImg: 'trailblazer',
                nanoukSays: "Woof! Every pack needs a goal. What's yours?",
                options: [
                    { value: 'weight_loss', label: 'Lose weight', emoji: '🔥' },
                    { value: 'muscle', label: 'Build muscle', emoji: '💪' },
                    { value: 'energy', label: 'More energy', emoji: '⚡' },
                    { value: 'health', label: 'Live healthier', emoji: '🌿' },
                    { value: 'sleep', label: 'Sleep better', emoji: '😴' }
                ]
            },
            {
                id: 'fitness_level',
                question: "What's your current fitness level?",
                icon: Dumbbell,
                nanoukImg: 'smart',
                nanoukSays: "Don't worry, I'll match your pace!",
                options: [
                    { value: 'beginner', label: 'Beginner', emoji: '🌱' },
                    { value: 'intermediate', label: 'Intermediate', emoji: '🏃' },
                    { value: 'advanced', label: 'Advanced', emoji: '🏆' }
                ]
            },
            {
                id: 'sleep_hours',
                question: "How many hours do you sleep on average?",
                icon: Moon,
                nanoukImg: 'sleeping',
                nanoukSays: "*yawns* Sleep is important for both of us...",
                options: [
                    { value: '5', label: 'Under 6 hours', emoji: '😵' },
                    { value: '6', label: '6-7 hours', emoji: '😐' },
                    { value: '7', label: '7-8 hours', emoji: '😊' },
                    { value: '8', label: 'Over 8 hours', emoji: '😴' }
                ]
            },
            {
                id: 'nutrition_style',
                question: "What's your diet like?",
                icon: Apple,
                nanoukImg: 'happy',
                nanoukSays: "Treats... I mean, healthy food is important!",
                options: [
                    { value: 'mixed', label: 'Mixed diet', emoji: '🍽️' },
                    { value: 'vegetarian', label: 'Vegetarian', emoji: '🥗' },
                    { value: 'vegan', label: 'Vegan', emoji: '🌱' },
                    { value: 'low_carb', label: 'Low Carb', emoji: '🥩' },
                    { value: 'intuitive', label: 'Intuitive', emoji: '🧘' }
                ]
            },
            {
                id: 'motivation',
                question: "What motivates you the most?",
                icon: Heart,
                nanoukImg: 'trailblazer',
                nanoukSays: "Together we can do anything, Buddy!",
                options: [
                    { value: 'streaks', label: 'Daily streaks', emoji: '🔥' },
                    { value: 'progress', label: 'Visible progress', emoji: '📈' },
                    { value: 'community', label: 'Community & challenges', emoji: '👥' },
                    { value: 'rewards', label: 'Rewards & badges', emoji: '🏅' },
                    { value: 'health', label: 'Health & wellbeing', emoji: '❤️' }
                ]
            }
        ],
        complete: {
            title: "Perfect, Buddy!",
            subtitle: "I know you better now. Let's get started!",
            nanoukSays: "Woof! We're a great team!"
        }
    }
};

export default function BuddyCheckQuiz({ language = 'de', onComplete, onClose }) {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [isComplete, setIsComplete] = useState(false);
    
    const t = quizContent[language] || quizContent.de;
    const questions = t.questions;
    const progress = ((currentQuestion + 1) / questions.length) * 100;
    
    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
        
        // Auto-advance after short delay
        setTimeout(() => {
            if (currentQuestion < questions.length - 1) {
                setCurrentQuestion(prev => prev + 1);
            } else {
                setIsComplete(true);
            }
        }, 300);
    };
    
    const handleBack = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(prev => prev - 1);
        }
    };
    
    const handleComplete = () => {
        if (onComplete) {
            onComplete(answers);
        }
    };
    
    const currentQ = questions[currentQuestion];
    const IconComponent = currentQ?.icon;
    
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && onClose?.()}
            data-testid="buddy-check-quiz"
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="w-full max-w-lg"
            >
                <Card className="bg-secondary border-border shadow-2xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                <Brain className="w-5 h-5 text-foreground" />
                                <span className="font-semibold text-foreground">{t.title}</span>
                            </div>
                            <Badge variant="secondary" className="bg-white/20 text-foreground">
                                {currentQuestion + 1}/{questions.length}
                            </Badge>
                        </div>
                        <Progress value={progress} className="h-2 bg-white/20" />
                    </div>
                    
                    <CardContent className="p-6">
                        <AnimatePresence mode="wait">
                            {!isComplete ? (
                                <motion.div
                                    key={currentQuestion}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {/* Nanouk */}
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 border-cyan-500/50">
                                            <img 
                                                src={NANOUK[currentQ.nanoukImg]} 
                                                alt="Nanouk" 
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="bg-muted/50 rounded-2xl rounded-tl-none p-3 flex-1">
                                            <p className="text-sm text-muted-foreground italic">
                                                "{currentQ.nanoukSays}"
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Question */}
                                    <div className="flex items-center gap-3 mb-4">
                                        {IconComponent && (
                                            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                                                <IconComponent className="w-5 h-5 text-primary" />
                                            </div>
                                        )}
                                        <h3 className="text-lg font-semibold text-foreground">
                                            {currentQ.question}
                                        </h3>
                                    </div>
                                    
                                    {/* Options */}
                                    <div className="space-y-2">
                                        {currentQ.options.map((option) => (
                                            <motion.button
                                                key={option.value}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => handleAnswer(currentQ.id, option.value)}
                                                className={`w-full p-3 rounded-xl border-2 transition-all flex items-center gap-3 ${
                                                    answers[currentQ.id] === option.value
                                                        ? 'border-cyan-500 bg-primary/20 text-foreground'
                                                        : 'border-border bg-muted/50 text-muted-foreground hover:border-slate-500'
                                                }`}
                                                data-testid={`quiz-option-${option.value}`}
                                            >
                                                <span className="text-xl">{option.emoji}</span>
                                                <span className="font-medium">{option.label}</span>
                                                {answers[currentQ.id] === option.value && (
                                                    <Check className="w-5 h-5 text-primary ml-auto" />
                                                )}
                                            </motion.button>
                                        ))}
                                    </div>
                                    
                                    {/* Navigation */}
                                    <div className="flex justify-between mt-6">
                                        <Button
                                            variant="ghost"
                                            onClick={handleBack}
                                            disabled={currentQuestion === 0}
                                            className="text-muted-foreground"
                                        >
                                            <ChevronLeft className="w-4 h-4 mr-1" />
                                            {language === 'de' ? 'Zurück' : 'Back'}
                                        </Button>
                                        {answers[currentQ.id] && currentQuestion < questions.length - 1 && (
                                            <Button
                                                onClick={() => setCurrentQuestion(prev => prev + 1)}
                                                className="bg-cyan-600 hover:bg-cyan-700"
                                            >
                                                {language === 'de' ? 'Weiter' : 'Next'}
                                                <ChevronRight className="w-4 h-4 ml-1" />
                                            </Button>
                                        )}
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="text-center py-4"
                                >
                                    {/* Completion */}
                                    <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-4 border-cyan-500 shadow-lg shadow-cyan-500/30">
                                        <img 
                                            src={NANOUK.happy} 
                                            alt="Nanouk Happy" 
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    
                                    <div className="mb-4">
                                        <Sparkles className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                                        <h3 className="text-2xl font-bold text-foreground mb-1">
                                            {t.complete.title}
                                        </h3>
                                        <p className="text-muted-foreground">{t.complete.subtitle}</p>
                                    </div>
                                    
                                    <div className="bg-muted/50 rounded-xl p-4 mb-6">
                                        <p className="text-primary italic">
                                            "{t.complete.nanoukSays}"
                                        </p>
                                    </div>
                                    
                                    <Button
                                        onClick={handleComplete}
                                        size="lg"
                                        className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                                        data-testid="quiz-complete-btn"
                                    >
                                        <Sparkles className="w-5 h-5 mr-2" />
                                        {language === 'de' ? 'Los geht\'s!' : 'Let\'s go!'}
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </CardContent>
                </Card>
            </motion.div>
        </motion.div>
    );
}
